import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { TourOutlined, CheckCircleOutline } from "@mui/icons-material";
import { TOUR_META, useProductTourOptional } from "../../common/tours";

/**
 * Help → Product tours — restart any guided walkthrough.
 */
export default function ProductToursPanel({ lang }) {
  const theme = useTheme();
  const tour = useProductTourOptional();

  if (!tour) return null;

  const catalog = tour.tourCatalog || Object.values(TOUR_META);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        mb: 2,
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <TourOutlined fontSize="small" />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>
              {lang?.tourPanelTitle || "Product tours"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lang?.tourPanelSubtitle ||
                "Replay a guided walkthrough anytime. Tours also start automatically the first time you visit a page."}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1.25}>
          {catalog.map((item) => {
            const done = tour.isCompleted(item.id);
            return (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.text.primary, 0.03),
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={700}>
                      {lang?.[item.titleKey] || item.titleFallback}
                    </Typography>
                    {done ? (
                      <Chip
                        size="small"
                        icon={<CheckCircleOutline sx={{ fontSize: "14px !important" }} />}
                        label={lang?.tourCompleted || "Completed"}
                        sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label={lang?.tourNotStarted || "Not started"}
                        variant="outlined"
                        sx={{ height: 22, fontWeight: 600, fontSize: "0.68rem" }}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {lang?.[item.descriptionKey] || item.descriptionFallback}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant={done ? "outlined" : "contained"}
                  startIcon={<TourOutlined />}
                  disabled={tour.isRunning}
                  onClick={() => tour.restartTour(item.id)}
                  sx={{ fontWeight: 700, textTransform: "none" }}
                >
                  {done
                    ? lang?.tourReplay || "Replay"
                    : lang?.tourStart || "Start tour"}
                </Button>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}
